import type {
    BottomSheetMaxSnapHeightConstantType,
    BottomSheetNaturalHeightConstantType,
    BottomSheetPivotHeightConstantType,
    BottomSheetSnapHeightConstantType,
    BottomSheetDragFrameConstantType,
    BottomSheetDragStartConstantType,
    BottomSheetScrollEndConstantType,
    BottomSheetMomentumConstantType,
    BottomSheetVelocityConstantType,
    BottomSheetGestureConstantsType,
    BottomSheetReleaseConstantType,
    BottomSheetHeightsConstantType,
    BottomSheetDragFrameType,
    BottomSheetMomentumType,
    BottomSheetReleaseType,
    BottomSheetHeightsType
} from "./type";

export const BOTTOM_SHEET_GESTURE: BottomSheetGestureConstantsType = {
    momentumDurationFactor: 2,
    momentumMaxDuration: 600,
    momentumVelocity: 0.1,
    snapLowerRatio: 0.33,
    snapUpperRatio: 0.66,
    momentumFactor: 350,
    snapZoneRatio: 0.2,
    fastVelocity: 0.5,
    edgeTolerance: 1,
    closeRatio: 0.5,
    snapZoneMin: 60,
    nativeSlop: 20,
    webSlop: 5
};

export const getBottomSheetHeights = ({
    isCanFullScreenOnSwipe,
    isWorkAsFullScreen,
    containerHeight,
    isAutoHeight,
    snapPoint,
    topInset,
    spaces
}: BottomSheetHeightsConstantType): BottomSheetHeightsType => {
    const availableHeight = Math.max(0, containerHeight - topInset);

    const isExpandable = isCanFullScreenOnSwipe || isWorkAsFullScreen;

    const maxHeight = isExpandable ? availableHeight : Math.max(0, availableHeight - spaces.spacingXxl);

    if(isWorkAsFullScreen) {
        return {
            fixedPivotHeight: availableHeight,
            isHeightControlled: true,
            availableHeight,
            isExpandable,
            maxHeight
        };
    }

    if(!isAutoHeight && snapPoint) {
        return {
            fixedPivotHeight: Math.min(snapPoint, maxHeight),
            isHeightControlled: true,
            availableHeight,
            isExpandable,
            maxHeight
        };
    }

    return {
        isHeightControlled: isCanFullScreenOnSwipe,
        availableHeight,
        isExpandable,
        maxHeight
    };
};

export const getBottomSheetNaturalHeight = ({
    contentHeight,
    sheetHeight,
    bodyHeight
}: BottomSheetNaturalHeightConstantType): number | undefined => {
    if(contentHeight < 0 || sheetHeight < 0 || bodyHeight < 0) {
        return undefined;
    }

    return sheetHeight - bodyHeight + contentHeight;
};

export const getBottomSheetPivotHeight = ({
    isHeightControlled,
    fixedPivotHeight,
    naturalHeight,
    sheetHeight,
    maxHeight
}: BottomSheetPivotHeightConstantType): number => {
    if(fixedPivotHeight !== undefined) {
        return fixedPivotHeight;
    }

    if(!isHeightControlled || naturalHeight === undefined) {
        return Math.max(0, sheetHeight);
    }

    return Math.min(naturalHeight, maxHeight);
};

export const getBottomSheetMaxSnapHeight = ({
    availableHeight,
    isExpandable,
    pivotHeight
}: BottomSheetMaxSnapHeightConstantType): number => {
    if(!isExpandable) {
        return pivotHeight;
    }

    return Math.max(pivotHeight, availableHeight);
};

export const getBottomSheetIsDragStarted = ({
    slop,
    dx,
    dy
}: BottomSheetDragStartConstantType): boolean => {
    return Math.abs(dy) >= slop && Math.abs(dy) > Math.abs(dx);
};

export const getBottomSheetDragFrame = ({
    isCanFullScreenOnSwipe,
    isWorkAsFullScreen,
    startScrollOffset,
    startTranslateY,
    maxSnapHeight,
    pivotHeight,
    startHeight,
    maxScroll,
    dy
}: BottomSheetDragFrameConstantType): BottomSheetDragFrameType => {
    if(dy >= 0) {
        const scrollShift = Math.min(dy, startScrollOffset);

        const heightShift = startHeight > pivotHeight ? Math.min(dy - scrollShift, startHeight - pivotHeight) : 0;

        return {
            translateY: startTranslateY + dy - scrollShift - heightShift,
            scrollOffset: startScrollOffset - scrollShift,
            height: startHeight - heightShift
        };
    }

    const rise = -dy;

    const translateShift = Math.min(rise, Math.max(0, startTranslateY));

    const restoreShift = startHeight < pivotHeight ? Math.min(rise - translateShift, pivotHeight - startHeight) : 0;

    const isScrollable = isCanFullScreenOnSwipe || isWorkAsFullScreen || startHeight >= pivotHeight;

    const scrollShift = isScrollable ? Math.min(rise - translateShift - restoreShift, Math.max(0, maxScroll - startScrollOffset)) : 0;

    const growShift = isCanFullScreenOnSwipe ? rise - translateShift - restoreShift - scrollShift : 0;

    return {
        height: Math.max(startHeight, Math.min(maxSnapHeight, startHeight + restoreShift + growShift)),
        translateY: startTranslateY - translateShift,
        scrollOffset: startScrollOffset + scrollShift
    };
};

export const getBottomSheetSnapHeight = ({
    isCanFullScreenOnSwipe,
    maxSnapHeight,
    pivotHeight,
    velocity,
    height
}: BottomSheetSnapHeightConstantType): number => {
    if(!isCanFullScreenOnSwipe) {
        return pivotHeight;
    }

    if(velocity < -BOTTOM_SHEET_GESTURE.fastVelocity) {
        return maxSnapHeight;
    }

    if(velocity > BOTTOM_SHEET_GESTURE.fastVelocity) {
        return pivotHeight;
    }

    const range = maxSnapHeight - pivotHeight;

    const distance = height - pivotHeight;

    if(distance < range * BOTTOM_SHEET_GESTURE.snapLowerRatio) {
        return pivotHeight;
    }

    if(distance > range * BOTTOM_SHEET_GESTURE.snapUpperRatio) {
        return maxSnapHeight;
    }

    return maxSnapHeight - height < height - pivotHeight ? maxSnapHeight : pivotHeight;
};

export const getBottomSheetRelease = ({
    isCanFullScreenOnSwipe,
    maxSnapHeight,
    isFromChrome,
    isSwipeClose,
    scrollOffset,
    isCancelled,
    pivotHeight,
    translateY,
    maxScroll,
    velocity,
    height
}: BottomSheetReleaseConstantType): BottomSheetReleaseType => {
    const isAtMaxSnapHeight = height >= maxSnapHeight - BOTTOM_SHEET_GESTURE.edgeTolerance;

    const isContentScrolled = maxScroll > 0 && scrollOffset > BOTTOM_SHEET_GESTURE.edgeTolerance;

    if(!isCancelled && !isFromChrome && isAtMaxSnapHeight && isContentScrolled) {
        return {
            action: Math.abs(velocity) > BOTTOM_SHEET_GESTURE.momentumVelocity ? "momentum" : "settle",
            height
        };
    }

    const snapZone = Math.max(pivotHeight * BOTTOM_SHEET_GESTURE.snapZoneRatio, BOTTOM_SHEET_GESTURE.snapZoneMin);

    if(translateY <= snapZone) {
        return {
            height: getBottomSheetSnapHeight({
                velocity: isCancelled ? 0 : velocity,
                isCanFullScreenOnSwipe,
                maxSnapHeight,
                pivotHeight,
                height
            }),
            action: "snap"
        };
    }

    if(!isSwipeClose) {
        return {
            height: pivotHeight,
            action: "snap"
        };
    }

    const isClosing = !isCancelled && (velocity > BOTTOM_SHEET_GESTURE.fastVelocity || translateY > pivotHeight * BOTTOM_SHEET_GESTURE.closeRatio);

    return {
        action: isClosing ? "close" : "snap",
        height
    };
};

export const getBottomSheetMomentum = ({
    scrollOffset,
    maxScroll,
    velocity
}: BottomSheetMomentumConstantType): BottomSheetMomentumType => {
    const momentum = velocity * -BOTTOM_SHEET_GESTURE.momentumFactor;

    return {
        duration: Math.min(Math.abs(momentum) * BOTTOM_SHEET_GESTURE.momentumDurationFactor, BOTTOM_SHEET_GESTURE.momentumMaxDuration),
        scrollOffset: Math.max(0, Math.min(scrollOffset + momentum, maxScroll))
    };
};

export const getBottomSheetVelocity = ({
    previousTime,
    currentTime,
    previousY,
    currentY,
    velocity
}: BottomSheetVelocityConstantType): number => {
    if(currentTime <= previousTime) {
        return velocity;
    }

    return (currentY - previousY) / (currentTime - previousTime);
};

export const getBottomSheetIsScrollEnd = ({
    scrollEndThreshold,
    contentHeight,
    scrollOffset,
    bodyHeight
}: BottomSheetScrollEndConstantType): boolean => {
    const maxScroll = Math.max(0, contentHeight - bodyHeight);

    if(maxScroll <= 0) {
        return false;
    }

    return scrollOffset / maxScroll >= scrollEndThreshold;
};
